import { useEffect, useState } from "react";
import api from "../lib/api";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  facilityConditionLabel,
  facilityConditionTone,
  facilityOptionLabel,
  isConditionStale,
} from "../lib/facilities";

/**
 * Radix needs a non-empty string for every item, so "Not sure" is a
 * sentinel rather than `""` — the same move `ALL_CONTEXTS` makes in
 * `lib/sessionContext.js`. It is translated back to `null` on the way out and
 * never reaches the API.
 *
 * COPY IS #544's VOCABULARY (#546): fields and nets, never "pitch" — the
 * words on screen match the words in the database (`FACILITY_KINDS`), and
 * "Not sure" is a REAL ANSWER rather than a placeholder: the column is
 * nullable at a venue that has four surfaces precisely because "I played
 * here, not sure which one" is true and must be selectable. Nothing is
 * preselected beyond it.
 */
const NO_SURFACE = "__none__";

const CONDITION_TONE_CLASS = {
  good: "bg-emerald-50 text-emerald-700",
  fair: "bg-amber-50 text-amber-700",
  bad: "bg-red-50 text-red-700",
  unknown: "bg-slate-100 text-slate-600",
};

/**
 * Issue #1028 review, F2/F3 — the new-UI log-a-match step's own drawn
 * values (`DirectionA2Facility.dc.html`), used only when `tone="quiet"`.
 * LITERAL HEX, never a Tailwind `slate-*`/named class: `index.css`'s own
 * "Darker secondary text" block `!important`-remaps `text-slate-500/600`
 * app-wide, which is exactly what made the first attempt at this (named
 * classes) drift from the drawing — measured live, review F3. A literal
 * value can't drift with a remap it never references. The training
 * dialog's own call passes no `tone` and never sees these.
 */
const QUIET_LABEL_STYLE = { fontSize: 11, fontWeight: 600, color: "#64748B" };
// Issue #1028 review round 3, R2-F3 — the shadcn `Label` bakes
// `leading-none` (`line-height: 1`) into its own default class string
// (`ui/label.jsx`'s `labelVariants()`), which no inline `style` can
// override: `line-height` isn't `!important` there, but it IS the same
// property twMerge already knows how to replace when a conflicting
// utility is merged in — so the fix is a className, not a style prop,
// same mechanism `cn()` already uses for every other shadcn override in
// this codebase. Passed as `className`, never folded into a style object.
const QUIET_LABEL_CLASSNAME = "leading-normal";
const QUIET_HELP_STYLE = { fontSize: 12, fontWeight: 400, lineHeight: 1.5, color: "#64748B" };
// Issue #1028 review round 3, R2-F2 — `text-xs text-slate-500 mt-1`'s own
// `text-slate-500` resolves through `index.css`'s "Darker secondary text"
// block, which marks its remap `!important`. An inline `style` NEVER
// beats an `!important` stylesheet rule regardless of specificity — the
// review measured it live: "Optional."/"Set when this was logged." kept
// computing #475569 (the remap) under `QUIET_HELP_STYLE`'s own
// `color: "#64748B"`. The only fix is dropping the class, not raising the
// style's own precedence, which doesn't exist. See `quietHelpClassName`.
const QUIET_READONLY_VALUE_STYLE = { color: "#0F172A" };
const QUIET_CHIP_STYLE = { background: "#F1F5F9", color: "#475569", borderRadius: 6, padding: "2px 6px", fontSize: 11, fontWeight: 600 };

/** `text-slate-500 mt-1`'s own classes, minus `text-slate-500` when quiet
 * (R2-F2, above) — the inline colour style is passed alongside this, and
 * only wins once the `!important` class it used to fight is gone. */
function quietHelpClassName(quiet) {
  return quiet ? "text-xs mt-1" : "text-xs text-slate-500 mt-1";
}
// Row height, and the check-on-the-left/tinted-selected-row treatment for
// `[data-state="checked"]`/`[data-highlighted]`, are CSS (`index.css`,
// ".facility-quiet-item") — the check icon's own position is hardcoded in
// `ui/select.jsx`'s `SelectItem` JSX (`right-2`), not exposed as a prop, so
// it can only be moved by a descendant selector on this instance's own
// class, never by a className merge alone. Review F4.
const QUIET_ITEM_CLASSNAME = "facility-quiet-item min-h-[44px] pl-9 pr-3";

/**
 * One row: the net and what it is made of, plus its CONDITION where somebody
 * has judged it.
 *
 * The condition belongs here and not only on the ground page because of the
 * scenario #423 opens with — "a coach can schedule a session at a venue with
 * four nets and three of them unusable". Choosing between four nets is the
 * moment that fact is worth something, and a picker that hides it makes the
 * one choice it exists to inform without the one input that matters.
 *
 * Stale ratings are MARKED, not hidden (`facilities.js`,
 * `CONDITION_STALE_DAYS`): "needs repair, last looked at 14 months ago" is
 * more information than silence, and deciding for the coach that an old
 * observation is worthless is not this component's call.
 */
function FacilityOption({ facility, tone }) {
  const condition = facilityConditionLabel(facility.condition);
  const stale = isConditionStale(facility.condition_rated_at);
  // Issue #1028, D55 — the new-UI log-a-match step draws the condition as
  // a word on a quiet, colourless chip: ink on a faint card, never the
  // alert palette (green/amber/red) this component's own default keys off
  // the condition — that palette marks FAILURES on this screen, and a
  // condition is not one. `tone="quiet"` renders `QUIET_CHIP_STYLE`
  // (literal hex, review F3) instead of a `CONDITION_TONE_CLASS` lookup;
  // the training dialog's own call (no `tone` passed) is untouched — same
  // classes, same colours it always had.
  const quiet = tone === "quiet";
  return (
    <span className="flex items-center gap-2 min-w-0">
      <span className="truncate">{facilityOptionLabel(facility)}</span>
      {condition && (
        <span
          className={quiet ? "shrink-0" : `shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${CONDITION_TONE_CLASS[facilityConditionTone(facility.condition)]}`}
          style={quiet ? QUIET_CHIP_STYLE : undefined}
          data-testid={`ts-facility-condition-${facility.facility_id}`}
        >
          {condition}
          {stale ? " · old" : ""}
        </span>
      )}
    </span>
  );
}

/**
 * The surfaces registered at one venue, re-read whenever the venue changes.
 *
 * CLEARED BEFORE THE FETCH, NOT AFTER IT, and that ordering is the whole
 * reason this is a hook rather than four lines inline. Between picking a new
 * ground and its list arriving, the state still holds the PREVIOUS venue's
 * nets — and a dropdown that offers net 3 of the ground you just left is
 * precisely the cross-venue pair the API exists to refuse. Empty is the only
 * honest answer while the question is in flight.
 *
 * A FAILED READ IS EMPTY, NOT AN ERROR. `GET /grounds/:id/facilities` resolves
 * the ground under the caller's own visibility, so a player looking at a
 * session their coach scheduled at the COACH's private ground gets a 404 —
 * correctly, and it is not a fault to report. The component renders the
 * stored surface read-only in that case; see below.
 */
export function useGroundFacilities(groundId) {
  const [facilities, setFacilities] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setFacilities([]);
    if (!groundId) return undefined;
    api
      .get(`/grounds/${groundId}/facilities`)
      .then((r) => { if (!cancelled) setFacilities(r.data || []); })
      .catch(() => { if (!cancelled) setFacilities([]); });
    return () => { cancelled = true; };
  }, [groundId]);
  return facilities;
}

/**
 * FAC-06's second clause, on screen (issues #166, #423): **a facility can be
 * selected when scheduling a training session.**
 *
 * #423 landed the column and the pair validation and deliberately stopped
 * there — "the API accepts and returns `facility_id`, but no form offers it
 * yet". This is the form offering it.
 *
 * WHY A SELECT AND NOT A COMBOBOX. `GroundPicker` is a typeahead because the
 * grounds directory is unbounded, shared and searched: you are hunting a
 * venue among thousands, possibly one that does not exist yet. A venue's
 * surfaces are a CLOSED LIST OF THREE OR FOUR ROWS which the caller has
 * already narrowed to by picking the ground. There is nothing to search, and
 * nothing to add from here — a net you cannot find is a net nobody has
 * registered, and registering one is the ground-editing path (#165), not a
 * side effect of logging a session. So this is the same shadcn `Select` the
 * dialog already uses for category and context, at the same touch height, and
 * it introduces no fourth interaction pattern.
 *
 * WHEN IT IS NOT THERE AT ALL, which is most of the time and is the point:
 *
 *   - NO GROUND PICKED → nothing renders. A surface with no venue is half a
 *     place; the API refuses it (`facility_needs_ground`) and the CHECK
 *     constraint refuses it again, so offering it would be offering a save
 *     that cannot happen.
 *   - GROUND WITH NO REGISTERED SURFACES → nothing renders. This is the
 *     COMMON case — most venues have no catalogued nets — and "behaves
 *     exactly as it does today" is the requirement rather than a nicety. An
 *     empty dropdown reading "no surfaces" would add a control to every
 *     training form in the app in exchange for nothing.
 *
 * WHEN THE LIST CANNOT BE READ BUT A SURFACE IS ALREADY SET, the stored name
 * is shown READ-ONLY rather than as an empty control or a silent blank. See
 * `useGroundFacilities` for whose session that is and why the 404 is correct.
 * The id round-trips untouched, which is exactly what the API's unchanged-pair
 * path allows (`isUnchangedGround`, `api/src/lib/groundRef.ts`).
 */
export default function FacilityPicker({
  groundId = null,
  value = null,
  /**
   * The control's own test id (#546): the training dialog keeps the default;
   * the log-a-match form passes `m-facility` so the two forms' controls stay
   * addressable when both exist in one app.
   */
  testId = "ts-facility",
  /**
   * The stored surface's own label, from the session row's `facility_name` /
   * `facility_surface` (#166) — used only when the venue's list is
   * unreadable.
   */
  storedLabel = null,
  /**
   * `(facilityId | null)`. Null means "no specific surface", which is a
   * legitimate answer at a venue that has four of them — not a placeholder.
   */
  onChange,
  disabled = false,
  /**
   * Issue #1028, D55 — `"quiet"` draws every condition chip in one flat,
   * colourless class rather than the green/amber/red keyed off the
   * condition itself. `undefined` (every call site before this issue,
   * including the training dialog's) keeps the original colours exactly.
   */
  tone,
}) {
  const facilities = useGroundFacilities(groundId);
  const quiet = tone === "quiet";

  if (!groundId) return null;

  if (facilities.length === 0) {
    if (!value || !storedLabel) return null;
    return (
      <div data-testid={`${testId}-readonly-block`}>
        <Label className={quiet ? QUIET_LABEL_CLASSNAME : undefined} style={quiet ? QUIET_LABEL_STYLE : undefined}>Field or net</Label>
        {/* R2-F3 — the value itself reads ink (#0F172A) on the drawn
            read-only state, not `text-slate-700`'s own remapped shade;
            same drop-the-class move the help line below needs for its own
            colour, and the same reason (`!important` beats any style). */}
        <p
          className={quiet ? "mt-1.5 text-sm" : "mt-1.5 text-sm text-slate-700"}
          style={quiet ? QUIET_READONLY_VALUE_STYLE : undefined}
          data-testid={`${testId}-readonly`}
        >
          {storedLabel}
        </p>
        <p className={quietHelpClassName(quiet)} style={quiet ? QUIET_HELP_STYLE : undefined}>Set when this was logged.</p>
      </div>
    );
  }

  /**
   * Issue #1028 review, F1 (blocker) — a value the CURRENT list doesn't
   * contain (the facility was archived since the match was logged;
   * #166 FAC-11's own query drops archived rows) must not be silently
   * coerced to "Not sure". The previous version of this line did exactly
   * that, and it is worse than the blank trigger it was written to avoid:
   * Radix suppresses `onValueChange` when the clicked item is already the
   * one selected, so with the trigger showing "Not sure" but the ROOT's
   * `value` secretly still `NO_SURFACE`-by-coercion, clicking "Not sure"
   * is a no-op click — the stale id survives every save afterwards and
   * the player has no control that can ever clear it (watched live:
   * `archivedClear` in the review's own B-on.json still carried the old
   * facility_id after that exact click).
   *
   * The fix gives the stored value its OWN selectable item — distinct
   * from "Not sure" — so `current` is always the real value (never a
   * silent substitute) and picking "Not sure" is a genuine change Radix
   * fires. `storedMissing` is independent of `tone`: this is a data-
   * correctness fix, not a new-screen-only one, since the training
   * dialog shares this exact code path.
   */
  const storedMissing = !!value && !facilities.some((f) => f.facility_id === value);
  const current = value || NO_SURFACE;
  const itemClassName = quiet ? QUIET_ITEM_CLASSNAME : undefined;

  return (
    <div data-testid={`${testId}-block`}>
      <Label className={quiet ? QUIET_LABEL_CLASSNAME : undefined} style={quiet ? QUIET_LABEL_STYLE : undefined}>Which field or net?</Label>
      <Select value={current} disabled={disabled} onValueChange={(v) => onChange(v === NO_SURFACE ? null : v)}>
        <SelectTrigger data-testid={testId} className="mt-1.5 h-11 sm:h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent data-testid={`${testId}-list`} className={quiet ? "facility-quiet-list" : undefined}>
          {/* Not a placeholder: "I played here, not sure which one" is a
              true answer, and the column is nullable for exactly that. */}
          <SelectItem value={NO_SURFACE} className={itemClassName} data-testid={`${testId}-option-none`}>Not sure</SelectItem>
          {storedMissing && (
            // F1 — the archived/no-longer-listed stored value, selectable
            // in its own right so choosing "Not sure" next to it is a real
            // value change. `storedLabel` is the same prop the read-only
            // (unreadable-list) branch above already uses.
            <SelectItem value={value} className={itemClassName} data-testid={`${testId}-option-stored`}>
              {storedLabel || "No longer listed"}
            </SelectItem>
          )}
          {facilities.map((f) => (
            <SelectItem key={f.facility_id} value={f.facility_id} className={itemClassName} data-testid={`${testId}-option-${f.name}`}>
              <FacilityOption facility={f} tone={tone} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className={quietHelpClassName(quiet)} style={quiet ? QUIET_HELP_STYLE : undefined}>Optional.</p>
    </div>
  );
}
